import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowRight, Play, Send, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function Hero() {
  const {
    t
  } = useLanguage();
  const {
    scrollY
  } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, 100]);
  const y2 = useTransform(scrollY, [0, 500], [0, -100]);
  const [step, setStep] = useState(0);
  const [typedText, setTypedText] = useState('');
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const runSequence = async () => {
      setStep(0);
      setTypedText('');
      const text = t.hero.aiPrompt;
      for (let i = 0; i <= text.length; i++) {
        await new Promise(r => setTimeout(r, 50));
        setTypedText(text.slice(0, i));
      }
      await new Promise(r => setTimeout(r, 500));
      setStep(1);
      await new Promise(r => setTimeout(r, 600));
      setStep(2);
      await new Promise(r => setTimeout(r, 2000));
      setStep(3);
      await new Promise(r => setTimeout(r, 4000));
      runSequence();
    };
    runSequence();
    return () => clearTimeout(timeout);
  }, [t.hero.aiPrompt]);
  return <section className="relative overflow-hidden bg-white dark:bg-dark-bg transition-colors duration-300 pt-24 pb-32 lg:pt-32 lg:pb-40" data-id="element-158">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-159">
        <motion.div style={{
        y: y1
      }} animate={{
        y: [0, -30, 0],
        rotate: [0, 10, 0]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-20 right-[10%] w-32 h-32 rounded-full bg-accent-blue/20 blur-3xl dark:bg-accent-blue/10" data-id="element-160" />
        <motion.div style={{
        y: y2
      }} animate={{
        rotate: [0, 90, 180],
        scale: [1, 1.1, 1]
      }} transition={{
        duration: 15,
        repeat: Infinity,
        ease: 'linear'
      }} className="absolute top-40 left-[5%] w-24 h-24 bg-purple-500/20 blur-3xl dark:bg-purple-500/10" data-id="element-161" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10" data-id="element-162">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} data-id="element-163">
          <motion.span initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="inline-flex items-center gap-2 py-1.5 px-4 rounded-full bg-accent-light dark:bg-accent-blue/10 border border-accent-blue/20 text-accent-blue text-sm font-medium mb-8" data-id="element-164">
            <span className="w-2 h-2 rounded-full bg-accent-blue animate-pulse" data-id="element-165"></span>
            {t.hero.badge}
          </motion.span>

          <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-gray-900 dark:text-white mb-8 leading-tight" data-id="element-166">
            {t.hero.title}
          </h1>

          <p className="mt-8 text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed" data-id="element-167">
            {t.hero.subtitle}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4" data-id="element-168">
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.98
          }} className="px-8 py-4 bg-accent-blue text-white rounded-xl font-semibold shadow-lg shadow-accent-blue/30 hover:shadow-xl hover:shadow-accent-blue/40 transition-all duration-300 flex items-center justify-center gap-2" data-id="element-169">
              {t.hero.cta}
              <ArrowRight className="w-4 h-4" data-id="element-170" />
            </motion.button>
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.98
          }} className="px-8 py-4 bg-white dark:bg-dark-card text-gray-700 dark:text-white border border-gray-200 dark:border-gray-700 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-all duration-300 flex items-center justify-center gap-2" data-id="element-171">
              <Play className="w-4 h-4 fill-current" data-id="element-172" />
              {t.hero.demo}
            </motion.button>
          </div>
        </motion.div>

        {/* AI Demo Animation */}
        <motion.div initial={{
        opacity: 0,
        y: 60
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.4
      }} className="mt-24 mx-auto max-w-5xl" data-id="element-173">
          <div className="relative group" data-id="element-174">
            <div className="absolute -inset-4 bg-gradient-to-r from-accent-blue/30 to-purple-500/30 rounded-3xl blur-2xl opacity-50 group-hover:opacity-70 transition-opacity duration-500" data-id="element-175"></div>

            <div className="relative bg-white dark:bg-dark-card rounded-2xl shadow-2xl border border-gray-200 dark:border-dark-border overflow-hidden min-h-[500px] flex flex-col md:flex-row" data-id="element-176">
              {/* Left: Chat */}
              <div className="w-full md:w-1/3 bg-gray-50 dark:bg-black/20 border-r border-gray-200 dark:border-dark-border flex flex-col p-6" data-id="element-177">
                <div className="flex items-center gap-3 mb-8" data-id="element-178">
                  <div className="w-8 h-8 bg-accent-blue rounded-lg flex items-center justify-center text-white" data-id="element-179">
                    <Sparkles className="w-4 h-4" data-id="element-180" />
                  </div>
                  <span className="font-bold text-gray-900 dark:text-white" data-id="element-181">
                    AI Builder
                  </span>
                </div>

                <div className="flex-1 space-y-4" data-id="element-182">
                  <div className="bg-white dark:bg-dark-bg p-4 rounded-2xl rounded-tl-none shadow-sm border border-gray-100 dark:border-dark-border text-sm text-gray-600 dark:text-gray-300" data-id="element-183">
                    Hi! What kind of website would you like to build today?
                  </div>
                  <div className="bg-accent-blue/10 dark:bg-accent-blue/20 p-4 rounded-2xl rounded-tr-none text-sm text-gray-800 dark:text-white ml-auto max-w-[90%] relative" data-id="element-184">
                    {typedText}
                    {step === 0 && <span className="animate-pulse" data-id="element-185">|</span>}
                  </div>
                </div>

                <div className="mt-auto relative" data-id="element-186">
                  <div className="w-full h-12 bg-white dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-xl px-4 flex items-center shadow-sm" data-id="element-187">
                    <span className="text-gray-400 text-sm truncate" data-id="element-188">
                      {step === 0 ? '' : t.hero.aiPrompt}
                    </span>
                  </div>
                  <motion.button animate={step === 1 ? {
                  scale: 0.9
                } : {
                  scale: 1
                }} className={`absolute right-2 top-2 w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${step >= 1 ? 'bg-accent-blue text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`} data-id="element-189">
                    <Send className="w-4 h-4" data-id="element-190" />
                  </motion.button>
                </div>
              </div>

              {/* Right: Preview */}
              <div className="w-full md:w-2/3 bg-gray-100/50 dark:bg-black/40 relative overflow-hidden flex items-center justify-center" data-id="element-191">
                <AnimatePresence mode="wait" data-id="element-192">
                  {step < 2 && <motion.div key="empty" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} className="text-center text-gray-400 dark:text-gray-500" data-id="element-193">
                      <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-50" data-id="element-194" />
                      <p data-id="element-195">Waiting for prompt...</p>
                    </motion.div>}

                  {step === 2 && <motion.div key="loading" initial={{
                  opacity: 0,
                  scale: 0.9
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 1.1
                }} className="text-center" data-id="element-196">
                      <div className="relative w-20 h-20 mx-auto mb-6" data-id="element-197">
                        <div className="absolute inset-0 border-4 border-gray-200 dark:border-gray-700 rounded-full" data-id="element-198"></div>
                        <div className="absolute inset-0 border-4 border-accent-blue rounded-full border-t-transparent animate-spin" data-id="element-199"></div>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-200">
                        {t.hero.aiWorking}
                      </h3>
                    </motion.div>}

                  {step === 3 && <motion.div key="result" initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="w-full h-full p-8 overflow-y-auto" data-id="element-201">
                      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 transform scale-95 origin-top" data-id="element-202">
                        <div className="h-16 border-b border-gray-100 flex items-center justify-between px-6" data-id="element-203">
                          <div className="font-bold text-lg text-gray-900" data-id="element-204">
                            Startup.io
                          </div>
                          <div className="flex gap-4 text-sm font-medium text-gray-600" data-id="element-205">
                            <span data-id="element-206">Product</span>
                            <span data-id="element-207">Pricing</span>
                            <span className="px-3 py-1 bg-black text-white rounded-lg text-xs flex items-center" data-id="element-208">
                              Get Started
                            </span>
                          </div>
                        </div>
                        <div className="p-8 text-center bg-gradient-to-b from-white to-gray-50" data-id="element-209">
                          <h2 className="text-3xl font-bold mb-4 text-gray-900" data-id="element-210">
                            Build Faster with AI
                          </h2>
                          <p className="text-gray-600 mb-6 max-w-md mx-auto text-sm" data-id="element-211">
                            Launch your startup in minutes.
                          </p>
                          <div className="flex justify-center gap-3" data-id="element-212">
                            <div className="h-8 w-24 bg-blue-600 rounded-lg" data-id="element-213"></div>
                            <div className="h-8 w-24 bg-gray-200 rounded-lg" data-id="element-214"></div>
                          </div>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}